import { useEffect, useMemo } from 'react';

import { useGetSensorsAndAncestors } from '@restDataAccess';
import { InventoryProvider, InventoryTree } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { OptionSelectModal } from '@/components/OptionSelectModal.tsx';
import { ProviderSensorWidgetConfig } from '@/components/ProviderSensorConfig.tsx';
import {
  useWidgetSitePanelCtx,
  useWidgetSiteTabbedShellCtx,
} from '@/components/widgetConfigSiteTabbedShell/index.ts';
import { useLayoutProps } from '@/hooks';
import { WidgetConfigModalProps } from '@/types.ts';
import { groupSensorOptionsByType } from '@/utils';

import { useSiteContext } from '../../providers';

type SensorByProviderSelectorProps = Pick<WidgetConfigModalProps, 'setModalTitle'> & {
  provider?: InventoryProvider;
};

export const SensorByProviderSelector = ({
  provider,
  setModalTitle,
}: SensorByProviderSelectorProps) => {
  const { t } = useTranslation(['common']);
  const { onSave } = useWidgetSitePanelCtx();
  const { shellWidgetId } = useWidgetSiteTabbedShellCtx();
  const { config, update } = useLayoutProps<ProviderSensorWidgetConfig | undefined>({
    widgetId: shellWidgetId,
    defaultConfig: undefined,
  });
  const { currentSite: { id: inventoryId } = {} } = useSiteContext();
  const { data: sensors = [], isLoading } = useGetSensorsAndAncestors({ provider, inventoryId });

  const sensorOptions = useMemo(
    () => groupSensorOptionsByType(sensors, sensor => sensor),
    [sensors]
  );

  useEffect(() => {
    if (setModalTitle) {
      setModalTitle(t('selectSensor'));
    }
  }, [setModalTitle, t]);

  return (
    <OptionSelectModal
      isLoading={isLoading}
      options={sensorOptions}
      label={t('availableSensors')}
      placeholder={sensors.find(sensor => sensor.id === config?.id)?.name ?? t('selectSensor')}
      onSave={inventoryTree => {
        const value = inventoryTree as InventoryTree;
        update({ widgetId: shellWidgetId, config: { id: value.id, name: value.name } });
        onSave(value);
      }}
    />
  );
};
